import { useCallback } from 'react';
import {
  BanIcon,
  BellIcon,
  Calendar,
  Check,
  ClockIcon,
  DocumentIcon,
  Euro,
  InformationCircle,
  LinkIcon,
  Mail,
  NoteIcon,
  RefreshIcon,
  SendIcon,
  TrashIcon,
  Warning,
  WarningCircle,
} from 'assets/icons';
import Button from 'components/button';
import UseContext from 'hooks/useContext';
import {
  authorMention,
  formatEventAt,
  groupEventsByMonth,
  historyEventTexts,
} from './invoiceHistoryText';
import useInvoiceHistory from './useInvoiceHistory';

const NEUTRAL = {
  Icon: InformationCircle,
  color: '#4B5563',
  box: 'bg-gray-100',
};

// The icon of each type, in a square tinted like the mockup (4.1).
const EVENT_ICONS = {
  fetched: { Icon: DocumentIcon, color: '#4B5563', box: 'bg-gray-100' },
  imported: { Icon: Check, color: '#15803D', box: 'bg-green-50' },
  import_failed: { Icon: WarningCircle, color: '#B91C1C', box: 'bg-red-50' },
  pennylane_category_set: {
    Icon: NoteIcon,
    color: '#4B5563',
    box: 'bg-gray-100',
  },
  category_changed: { Icon: NoteIcon, color: '#4B5563', box: 'bg-gray-100' },
  payment_term_changed: {
    Icon: Calendar,
    color: '#4B5563',
    box: 'bg-gray-100',
  },
  payment_status_changed: { Icon: Euro, color: '#4B5563', box: 'bg-gray-100' },
  appendix_added: { Icon: LinkIcon, color: '#4B5563', box: 'bg-gray-100' },
  appendix_sent: { Icon: SendIcon, color: '#15803D', box: 'bg-green-50' },
  appendix_deleted: { Icon: TrashIcon, color: '#4B5563', box: 'bg-gray-100' },
  comment_deleted: { Icon: TrashIcon, color: '#4B5563', box: 'bg-gray-100' },
  sent_by_mail: { Icon: Mail, color: '#4B5563', box: 'bg-gray-100' },
  reminder_sent: { Icon: BellIcon, color: '#B45309', box: 'bg-yellow-50' },
  litigation_opened: { Icon: Warning, color: '#B91C1C', box: 'bg-red-50' },
  litigation_closed: { Icon: Check, color: '#4B5563', box: 'bg-gray-100' },
  reminders_suspended: { Icon: BanIcon, color: '#4B5563', box: 'bg-gray-100' },
  reminders_resumed: { Icon: RefreshIcon, color: '#15803D', box: 'bg-green-50' },
};

const iconOf = (type) =>
  Object.prototype.hasOwnProperty.call(EVENT_ICONS, type)
    ? EVENT_ICONS[type]
    : NEUTRAL;

// As the unfolded rows of the grouped invoice list: dashed between the rows,
// solid under the last one, which closes the history.
const SOLID_BORDER = 'border-b border-gray-100';
const DASHED_BORDER = 'border-b border-dashed border-gray-200';

const EventRow = ({ icon, label, details = [], footnote, isLast }) => {
  const { Icon, color, box } = icon;
  // A label alone is shorter than its icon: it sits on the icon's middle
  // rather than on its top edge.
  const isSingleLine = details.length === 0 && !footnote;
  return (
    <li
      className={`flex gap-3 ${isLast ? SOLID_BORDER : DASHED_BORDER} px-4 md:px-6 py-3 ${
        isSingleLine ? 'items-center' : ''
      }`}
    >
      <div
        aria-hidden
        className={`flex flex-shrink-0 items-center justify-center w-9 h-9 rounded-md ${box}`}
      >
        <Icon wh={18} color={color} />
      </div>
      <div className="flex flex-col min-w-0">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {details.map((line) => (
          <div key={line} className="text-sm text-gray-700">
            {line}
          </div>
        ))}
        {footnote && <div className="text-sm text-gray-500">{footnote}</div>}
      </div>
    </li>
  );
};

// One month of events under its header, read like a table: the header sticks
// to the top of the panel, on white, while the rows scroll under it.
const MonthGroup = ({ label, children }) => (
  <section>
    <h4 className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 md:px-6 py-3.5 text-left text-sm font-medium uppercase text-gray-700 whitespace-nowrap">
      {label}
    </h4>
    <ul>{children}</ul>
  </section>
);

/**
 * The "Historique" tab of an invoice's detail (JBWEB-1278, mockup 4.1): its
 * events by month, newest first. The history starts with 1278, without any
 * catch-up (api README, decision 43): a migrated invoice says that what came
 * before is not known.
 */
const InvoiceHistoryTab = ({ invoice, categories, refreshKey }) => {
  const { language } = UseContext();
  const text = useCallback(
    (key, fallback) => language.getValue(`invoiceHistory.${key}`, fallback),
    [language],
  );
  const texts = {
    text,
    journalText: (key, fallback) =>
      language.getValue(`invoiceJournal.${key}`, fallback),
    listText: (key, fallback) =>
      language.getValue(`invoiceList.${key}`, fallback),
  };
  const history = useInvoiceHistory(invoice.number, refreshKey);

  if (history.failure) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 px-4 py-16 text-center text-sm text-gray-700"
      >
        <div>
          {history.failure === 'notFound'
            ? text(
                'notFound',
                'Cette facture est introuvable : elle n’a pas d’historique.',
              )
            : text('loadFailed', 'L’historique n’a pas pu être chargé.')}
        </div>
        {history.failure === 'failed' && (
          <Button color="SECONDARY" onClick={history.reload}>
            {text('retryLoad', 'Réessayer')}
          </Button>
        )}
      </div>
    );
  }
  if (!history.events) {
    return (
      <div
        role="status"
        className="px-4 py-16 text-center text-sm text-gray-500"
      >
        {text('loading', 'Chargement de l’historique…')}
      </div>
    );
  }

  const isMigrated = invoice.origin === 'migrated';
  if (history.events.length === 0 && !isMigrated) {
    return (
      <div className="px-4 py-16 text-center text-sm text-gray-500">
        {text('empty', 'Aucun évènement')}
      </div>
    );
  }

  const groups = groupEventsByMonth(text, history.events);
  // The last row closes the history: the migration line when there is one.
  const lastGroup = groups[groups.length - 1];
  const lastEventId = isMigrated
    ? null
    : lastGroup?.events[lastGroup.events.length - 1]?.id;
  return (
    <div className="flex flex-col">
      {groups.map((group) => (
        <MonthGroup key={group.key} label={group.label}>
          {group.events.map((event) => {
            const { label, details } = historyEventTexts(texts, event, {
              categories,
              language,
            });
            const footnote = [
              formatEventAt(text, event.at),
              authorMention(text, event),
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <EventRow
                key={event.id}
                icon={iconOf(event.type)}
                label={label}
                details={details}
                footnote={footnote}
                isLast={event.id === lastEventId}
              />
            );
          })}
        </MonthGroup>
      ))}
      {isMigrated && (
        <ul>
          <EventRow
            icon={{ Icon: ClockIcon, color: '#4B5563', box: 'bg-gray-100' }}
            label={text(
              'migrated',
              'Reprise de l’ancien import : historique antérieur indisponible',
            )}
            isLast
          />
        </ul>
      )}
    </div>
  );
};

export default InvoiceHistoryTab;
